import { useSyncExternalStore } from 'react';
import { useTranslation } from 'react-i18next';
import {
  BACKGROUND_MODES,
  DARK_BACKGROUND,
  DEFAULT_BACKGROUND,
  type BoardMeta,
  type SceneStore,
} from '@os/doc';
import { Button, Checkbox, ColorInput, Panel, RadioGroup, Select } from '../../ui';

/**
 * Board settings shared with everyone (background, grid snapping); applied at once, read-only for viewers. A `Panel`
 * (P16, backlog §19): the close button, Escape from anywhere and a click outside all close it; it takes the focus when
 * it opens and the editor gives it back to the canvas.
 */
/** Grid sizes offered (world px); 24 is the default and the dots' own step. */
export const GRID_SIZES = [8, 12, 16, 24, 32, 48] as const;

export function BoardSettings({
  store,
  onClose,
  snap,
}: {
  store: SceneStore;
  onClose: () => void;
  /** Snapping is personal in the editor (P16): this person's toggle; absent, the checkbox edits the board's default. */
  snap?: { on: boolean; toggle: () => void };
}) {
  const { t } = useTranslation();
  const meta = useSyncExternalStore(
    (cb) => store.subscribeMeta(cb),
    () => store.meta(),
  );
  const readOnly = useSyncExternalStore(
    (cb) => store.subscribeReadOnly(cb),
    () => store.readOnly,
  );
  const bg = meta.background;
  const setBg = (patch: Partial<BoardMeta['background']>) =>
    store.commands.setBoardMeta({ background: { ...bg, ...patch } });
  return (
    <Panel title={t('boardSettings.title')} closeLabel={t('boardSettings.close')} onClose={onClose} finalFocus={false}>
      <fieldset disabled={readOnly} className="flex flex-col gap-3">
        <RadioGroup
          variant="segmented"
          label={t('boardSettings.pattern')}
          value={bg.mode}
          disabled={readOnly}
          onValueChange={(mode) => setBg({ mode })}
          options={BACKGROUND_MODES.map((mode) => ({ value: mode, label: t(`boardSettings.mode.${mode}`) }))}
        />
        <ColorInput label={t('boardSettings.color')} value={bg.color} disabled={readOnly} onChange={(color) => setBg({ color })} />
        <ColorInput label={t('boardSettings.patternColor')} value={bg.patternColor} disabled={readOnly} onChange={(patternColor) => setBg({ patternColor })} />
        <div className="flex gap-2">
          <Button size="sm" className="flex-1" onClick={() => store.commands.setBoardMeta({ background: DEFAULT_BACKGROUND })}>
            {t('boardSettings.light')}
          </Button>
          <Button size="sm" className="flex-1" onClick={() => store.commands.setBoardMeta({ background: DARK_BACKGROUND })}>
            {t('boardSettings.dark')}
          </Button>
        </div>
        <label className="flex items-center justify-between gap-2">
          {t('boardSettings.gridSize')}
          <Select
            value={meta.grid.size}
            onChange={(e) => store.commands.setBoardMeta({ grid: { ...meta.grid, size: Number(e.target.value) } })}
            className="h-7 w-20"
          >
            {(GRID_SIZES as readonly number[]).includes(meta.grid.size) ? null : <option value={meta.grid.size}>{meta.grid.size}</option>}
            {GRID_SIZES.map((n) => (
              <option key={n} value={n}>
                {n}
              </option>
            ))}
          </Select>
        </label>
        <Checkbox
          label={snap ? t('boardSettings.snapGridMine') : t('boardSettings.snapGrid')}
          disabled={readOnly}
          checked={snap ? snap.on : meta.grid.snap}
          onCheckedChange={(on) => (snap ? snap.toggle() : store.commands.setBoardMeta({ grid: { ...meta.grid, snap: on } }))}
        />
      </fieldset>
    </Panel>
  );
}
